import React, { ReactNode } from 'react';
import clsx from 'clsx';
import { clickableTextClass } from '@components/RingilComponents/clickableText.ts';
import SvgExternalLink from '@icons/external-link.svg?react';
import SvgChevronRightSmall from '@icons/chevron-right-small.svg?react';
import SvgCheckCircleSolid from '@icons/check-circle-solid.svg?react';
import { AssistantDivider, AssistantSectionTitle } from './AssistantSection';
import { answerKey, toggleStruck, useStruckRows } from './assistantStruckStore';

/**
 * Lightweight markdown renderer for assistant answers (`content_markdown`).
 *
 * Deliberately small and dependency-free: it covers the subset the assistant actually emits —
 * headings, nested ordered/unordered lists, fenced code, blockquotes, rules, and inline
 * bold/italic/code/links. It renders straight to React elements rather than to an HTML string, so
 * there is no dangerouslySetInnerHTML and nothing to sanitize; anything it does not recognise falls
 * through as literal text.
 *
 * Styled to the answer design (Figma 21746:18244):
 *  - headings are the bold section titles ("Postup pro nalezení této možnosti"), with a divider above
 *    every one that does not open the answer;
 *  - numbered lists are steps, numbered in circles;
 *  - task lists (`- [ ] …` / `- [x] …`) are the "check this first" list, ticked in circles;
 *  - a line that is only a path — `Nastavení > Profil společnosti > Uživatelé` — is the breadcrumb chip;
 *  - links are brand text with an external-link mark, as they open in a new tab.
 *
 * Steps and checklist rows can be crossed out: clicking the marker or the row strikes the text through
 * and greys the marker, clicking again restores it (Figma comment on 21746:18244). Remembered per answer
 * in assistantStruckStore, so following a procedure step by step survives reloads and remounts.
 *
 * Not supported (yet): tables, images, reference links, inline HTML.
 */

/** Only these schemes become real links; anything else renders as plain text. */
const SAFE_LINK_SCHEME = /^(https?:|mailto:)/i;

// Alternation order matters: ** must be tried before *, or bold would match as two italics.
const INLINE_PATTERN = /(\*\*|__)([\s\S]+?)\1|(\*|_)([\s\S]+?)\3|`([^`]+)`|\[([^\]]*)\]\(([^\s)]+)\)|(https?:\/\/[^\s<)]+)/;

const renderLink = (href: string, label: string, key: string): ReactNode => {
  if (!SAFE_LINK_SCHEME.test(href)) return label;
  return (
    <a key={key} href={href} target="_blank" rel="noopener noreferrer" className={`${clickableTextClass} break-all no-underline`}>
      {label}
      <SvgExternalLink className="ml-1 inline-block h-3.5 w-3.5 align-[-2px]" />
    </a>
  );
};

const renderInline = (text: string, keyPrefix: string): ReactNode[] => {
  const nodes: ReactNode[] = [];
  let rest = text;
  let index = 0;

  while (rest.length > 0) {
    const match = INLINE_PATTERN.exec(rest);
    if (!match) {
      nodes.push(rest);
      break;
    }

    if (match.index > 0) nodes.push(rest.slice(0, match.index));
    const key = `${keyPrefix}-${index++}`;

    const bold = match[2];
    const italic = match[4];
    const code = match[5];
    const linkLabel = match[6];
    const linkHref = match[7];
    const autoLink = match[8];

    if (bold !== undefined) {
      nodes.push(<strong key={key}>{renderInline(bold, key)}</strong>);
    } else if (italic !== undefined) {
      nodes.push(<em key={key}>{renderInline(italic, key)}</em>);
    } else if (code !== undefined) {
      nodes.push(
        <code key={key} className="rounded bg-base-base px-1 py-0.5 font-mono text-xs">
          {code}
        </code>,
      );
    } else if (linkHref !== undefined) {
      nodes.push(renderLink(linkHref, linkLabel || linkHref, key));
    } else if (autoLink !== undefined) {
      nodes.push(renderLink(autoLink, autoLink, key));
    }

    rest = rest.slice(match.index + match[0].length);
  }

  return nodes;
};

/** A path through the app, e.g. `Nastavení > Profil společnosti > Uživatelé` (also →, ›, »). */
const BREADCRUMB_SEPARATOR = /\s+(?:>|→|›|»)\s+/;
/** Longer "segments" are prose that happens to contain a `>`, not menu names. */
const BREADCRUMB_SEGMENT_MAX = 40;

const breadcrumbOf = (line: string): string[] | null => {
  const parts = line
    .trim()
    .split(BREADCRUMB_SEPARATOR)
    // `**Nastavení** > **Uživatelé**` — emphasis on the segments is the chip's job.
    .map(part => part.replace(/^(\*\*|__)(.+)\1$/, '$2').trim());
  if (parts.length < 2 || parts.some(part => !part || part.length > BREADCRUMB_SEGMENT_MAX)) return null;
  return parts;
};

const Breadcrumb: React.FC<{ parts: string[] }> = ({ parts }) => (
  <div className="flex flex-wrap items-center gap-1.5 self-start rounded-lg bg-base-secondary px-2.5 py-1.5 text-xs font-medium text-content-primary">
    {parts.map((part, index) => (
      <React.Fragment key={index}>
        {index > 0 && <SvgChevronRightSmall className="h-2 w-2 shrink-0 text-content-secondary" />}
        <span>{part}</span>
      </React.Fragment>
    ))}
  </div>
);

const isFence = (line: string) => /^\s*```/.test(line);
const headingOf = (line: string) => /^(#{1,6})\s+(.*)$/.exec(line);
const quoteOf = (line: string) => /^\s*>\s?(.*)$/.exec(line);
const isRule = (line: string) => /^\s*([-*_])\s*(\1\s*){2,}$/.test(line);

interface ListNode {
  indent: number;
  ordered: boolean;
  /** Number the author wrote for an ordered item, so `3.` keeps starting at 3. */
  marker: number;
  /** A `- [ ]` / `- [x]` item — rendered as a ticked checklist row. */
  task: boolean;
  text: string;
  children: ListNode[];
}

/** Matches both `- item` and `1. item`, capturing the indent that drives nesting. */
const listItemOf = (line: string): ListNode | null => {
  const match = /^(\s*)(?:([-*+])|(\d+)[.)])\s+(.*)$/.exec(line);
  if (!match) return null;
  const ordered = match[3] !== undefined;
  const task = !ordered ? /^\[[ xX]\]\s+/.exec(match[4]) : null;
  return {
    indent: match[1].replace(/\t/g, '    ').length,
    ordered,
    marker: ordered ? Number(match[3]) : 0,
    task: !!task,
    text: task ? match[4].slice(task[0].length) : match[4],
    children: [],
  };
};

/** Turns a flat run of list items into a tree using their leading indentation. */
const nestByIndent = (items: ListNode[]): ListNode[] => {
  const root: ListNode[] = [];
  const stack: { indent: number; children: ListNode[] }[] = [{ indent: -1, children: root }];

  items.forEach(item => {
    while (stack.length > 1 && item.indent <= stack[stack.length - 1].indent) stack.pop();
    stack[stack.length - 1].children.push(item);
    stack.push(item);
  });

  return root;
};

type ListKind = 'ordered' | 'task' | 'bullet';
const kindOf = (item: ListNode): ListKind => (item.ordered ? 'ordered' : item.task ? 'task' : 'bullet');

/** Crossing-out state for the step and checklist rows of one answer, numbered in document order. */
interface RowContext {
  struck: number[];
  /** Absent while the answer is still streaming: rows are keyed by the final text, so none can be crossed out yet. */
  toggle?: (row: number) => void;
  /** Hands out the next row number — called once per row while rendering. */
  next: () => number;
}

/** The number circle of a step, or the tick of a checklist row — 21px, in line with the first line of text. Grey once crossed out. */
const StepMarker: React.FC<{ kind: 'ordered' | 'task'; number: number; struck: boolean }> = ({ kind, number, struck }) =>
  kind === 'ordered' ? (
    <span
      className={clsx(
        'flex h-[21px] w-[21px] shrink-0 items-center justify-center rounded-full text-sm font-semibold text-white transition-colors duration-200',
        struck ? 'bg-content-tertiary' : 'bg-complementary-processing',
      )}
    >
      {number}
    </span>
  ) : (
    <SvgCheckCircleSolid
      className={clsx('h-[21px] w-[21px] shrink-0 transition-colors duration-200', struck ? 'text-content-tertiary' : 'text-complementary-processing')}
    />
  );

interface StepRowProps {
  kind: 'ordered' | 'task';
  number: number;
  struck: boolean;
  /** Without it the row is static. */
  onToggle?: () => void;
}

/**
 * A step or checklist row that the user crosses out as they go. The whole row is the toggle — marker
 * and text alike — except for links inside it, which keep opening their page. A checkbox to assistive
 * tech, with Space / Enter doing what a click does.
 */
const StepRow: React.FC<React.PropsWithChildren<StepRowProps>> = ({ kind, number, struck, onToggle, children }) =>
  !onToggle ? (
    <li className="flex items-start gap-4">
      <StepMarker kind={kind} number={number} struck={false} />
      <div className="min-w-0 flex-1 pt-px">{children}</div>
    </li>
  ) : (
    <li
      role="checkbox"
      aria-checked={struck}
      tabIndex={0}
      onClick={e => {
        if ((e.target as HTMLElement).closest('a')) return;
        onToggle();
      }}
      onKeyDown={e => {
        if (e.key !== ' ' && e.key !== 'Enter') return;
        e.preventDefault();
        onToggle();
      }}
      className="flex cursor-pointer items-start gap-4 rounded outline-none focus-visible:ring-2 focus-visible:ring-content-brand/40"
    >
      <StepMarker kind={kind} number={number} struck={struck} />
      <div className={clsx('min-w-0 flex-1 pt-px transition-colors duration-200', struck && 'text-content-secondary line-through')}>{children}</div>
    </li>
  );

const renderListLevel = (items: ListNode[], keyPrefix: string, rows: RowContext, depth = 0): ReactNode[] => {
  const out: ReactNode[] = [];
  let i = 0;
  let group = 0;

  // A level can mix types (steps followed by bullets); each run becomes its own list.
  while (i < items.length) {
    const kind = kindOf(items[i]);
    const run: ListNode[] = [];
    while (i < items.length && kindOf(items[i]) === kind) run.push(items[i++]);

    const k = `${keyPrefix}-l${group++}`;
    const nested = (item: ListNode, index: number) => item.children.length > 0 && renderListLevel(item.children, `${k}-${index}`, rows, depth + 1);

    // Steps and checklists get the design's circle rows at the top level; nested inside another list
    // they fall back to plain numbering / bullets, where a second column of circles would be noise.
    if (depth === 0 && kind !== 'bullet') {
      const start = run[0].marker || 1;
      const List = kind === 'ordered' ? 'ol' : 'ul';
      out.push(
        <List key={k} className="my-0 flex list-none flex-col gap-4 p-0">
          {run.map((item, index) => {
            const row = rows.next();
            return (
              <StepRow key={`${k}-${index}`} kind={kind} number={start + index} struck={rows.struck.includes(row)} onToggle={rows.toggle && (() => rows.toggle(row))}>
                {renderInline(item.text, `${k}-${index}`)}
                {nested(item, index)}
              </StepRow>
            );
          })}
        </List>,
      );
      continue;
    }

    const children = run.map((item, index) => (
      <li key={`${k}-${index}`} className="my-0.5">
        {renderInline(item.text, `${k}-${index}`)}
        {nested(item, index)}
      </li>
    ));
    out.push(
      kind === 'ordered' ? (
        <ol key={k} start={run[0].marker || 1} className="my-0 list-decimal pl-5">
          {children}
        </ol>
      ) : (
        <ul key={k} className="my-0 list-disc pl-5">
          {children}
        </ul>
      ),
    );
  }

  return out;
};

const renderBlocks = (markdown: string, rows: RowContext): ReactNode[] => {
  const lines = (markdown ?? '').replace(/\r\n/g, '\n').split('\n');
  const blocks: ReactNode[] = [];
  let i = 0;
  let key = 0;
  let lastWasDivider = false;

  const nextKey = () => `b-${key++}`;

  while (i < lines.length) {
    const line = lines[i];

    // Fenced code block — content is taken verbatim, never parsed for inline markup.
    if (isFence(line)) {
      const code: string[] = [];
      i++;
      while (i < lines.length && !isFence(lines[i])) code.push(lines[i++]);
      i++; // consume the closing fence (or fall off the end for an unterminated block)
      blocks.push(
        <pre key={nextKey()} className="overflow-x-auto rounded bg-base-base p-2 font-mono text-xs text-content-primary">
          {code.join('\n')}
        </pre>,
      );
      continue;
    }

    if (!line.trim()) {
      i++;
      continue;
    }

    // Checked before lists so `---` is a rule rather than a bullet.
    if (isRule(line)) {
      if (!lastWasDivider) blocks.push(<AssistantDivider key={nextKey()} />);
      lastWasDivider = true;
      i++;
      continue;
    }

    const heading = headingOf(line);
    if (heading) {
      // Every level is the design's section title — inside a chat bubble a page-sized h1 reads as
      // shouting, and answers only use headings to title sections. One below other content is set off
      // by a divider, as in the design; one that opens the answer, or already follows a `---`, is not.
      if (blocks.length > 0 && !lastWasDivider) blocks.push(<AssistantDivider key={nextKey()} />);
      const k = nextKey();
      blocks.push(<AssistantSectionTitle key={k}>{renderInline(heading[2], k)}</AssistantSectionTitle>);
      lastWasDivider = false;
      i++;
      continue;
    }
    lastWasDivider = false;

    if (listItemOf(line)) {
      const items: ListNode[] = [];
      // Blank lines inside a list don't end it, as long as another item follows.
      while (i < lines.length) {
        const item = listItemOf(lines[i]);
        if (item) {
          items.push(item);
          i++;
        } else if (!lines[i].trim() && i + 1 < lines.length && listItemOf(lines[i + 1])) {
          i++;
        } else {
          break;
        }
      }
      const k = nextKey();
      blocks.push(<React.Fragment key={k}>{renderListLevel(nestByIndent(items), k, rows)}</React.Fragment>);
      continue;
    }

    if (quoteOf(line)) {
      const quoted: string[] = [];
      while (i < lines.length && quoteOf(lines[i])) quoted.push(quoteOf(lines[i++])[1]);
      const k = nextKey();
      blocks.push(
        <blockquote key={k} className="my-0 border-0 border-l-2 border-solid border-content-tertiary pl-3 text-content-secondary">
          {renderInline(quoted.join(' '), k)}
        </blockquote>,
      );
      continue;
    }

    // Plain paragraph: consecutive non-blank lines that start no other block.
    const paragraph: string[] = [];
    while (i < lines.length) {
      const current = lines[i];
      if (!current.trim() || isFence(current) || isRule(current) || headingOf(current) || listItemOf(current) || quoteOf(current)) break;
      paragraph.push(current);
      i++;
    }
    const k = nextKey();
    const breadcrumb = paragraph.length === 1 ? breadcrumbOf(paragraph[0]) : null;
    if (breadcrumb) {
      blocks.push(<Breadcrumb key={k} parts={breadcrumb} />);
      continue;
    }
    blocks.push(
      <p key={k} className="my-0">
        {paragraph.map((text, index) => (
          <React.Fragment key={`${k}-${index}`}>
            {index > 0 && <br />}
            {renderInline(text, `${k}-${index}`)}
          </React.Fragment>
        ))}
      </p>,
    );
  }

  return blocks;
};

interface ComponentProps {
  content: string;
  /** False while the answer is still streaming in — see RowContext.toggle. */
  interactive?: boolean;
}

const AssistantMarkdown: React.FC<ComponentProps> = ({ content, interactive = true }) => {
  const key = answerKey(content ?? '');
  const struck = useStruckRows(key);
  let counter = 0;
  const rows: RowContext = { struck: interactive ? struck : [], toggle: interactive ? row => toggleStruck(key, row) : undefined, next: () => counter++ };

  return <div className="flex w-full flex-col gap-4 break-words text-sm text-content-primary">{renderBlocks(content, rows)}</div>;
};

export default AssistantMarkdown;
